import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// [AW Comision]
const APPWHOOP_COMMISSION = 10; // 10% fijo
const IVA_RATE = 19; // 19% IVA en Chile
const MIN_PRICE = 3500; // Precio mínimo por ticket

export default function PriceCalculatorModal({
  isOpen,
  onClose,
  onSave,
  initialBasePrice = 0,
  initialFinalPrice = 0,
  ambassadorCommission = 0, // Comisión de embajadores desde la base de datos (0, 3, 5, 10, 15, 20)
}) {
  // Estado para el único input editable: Precio Final (con IVA)
  const [finalPrice, setFinalPrice] = useState(initialFinalPrice);
  // Estado para las pestañas (cuando hay embajadores)
  const [activeTab, setActiveTab] = useState('with-ambassador');

  // Estados para valores calculados (solo lectura)
  const [precioSinIva, setPrecioSinIva] = useState(0);
  const [utilidadNeta, setUtilidadNeta] = useState(0);
  const [utilidadNetaConIva, setUtilidadNetaConIva] = useState(0);
  const [comisionAW, setComisionAW] = useState(0);
  const [comisionAWConIva, setComisionAWConIva] = useState(0);
  const [comisionAmbassador, setComisionAmbassador] = useState(0);
  const [comisionAmbassadorConIva, setComisionAmbassadorConIva] = useState(0);

  // Convertir comisiones a decimales
  const ambassadorCommissionDecimal = ambassadorCommission / 100;
  const appwhoopCommissionDecimal = APPWHOOP_COMMISSION / 100;

  // Inicializar valores cuando se abre el modal
  useEffect(() => {
    if (isOpen) {
      setFinalPrice(initialFinalPrice || 0);
      if (initialFinalPrice > 0) {
        calculateFromFinalPrice(initialFinalPrice);
      } else {
        // Reset todos los valores
        setPrecioSinIva(0);
        setUtilidadNeta(0);
        setUtilidadNetaConIva(0);
        setComisionAW(0);
        setComisionAWConIva(0);
        setComisionAmbassador(0);
        setComisionAmbassadorConIva(0);
      }
    }
  }, [isOpen, initialFinalPrice]);

  // Función para calcular todos los valores desde el precio final
  const calculateFromFinalPrice = (newFinalPrice) => {
    const finalPriceNum = parseFloat(newFinalPrice) || 0;

    if (finalPriceNum <= 0) {
      setPrecioSinIva(0);
      setUtilidadNeta(0);
      setUtilidadNetaConIva(0);
      setComisionAW(0);
      setComisionAWConIva(0);
      setComisionAmbassador(0);
      setComisionAmbassadorConIva(0);
      return;
    }

    // Paso 2: Calcular ganancia neta con IVA (del precio final)
    // Porcentaje total de comisiones = 10% (AW) + % embajador
    const porcentajeTotalComisiones = APPWHOOP_COMMISSION + ambassadorCommission;
    const porcentajeGananciaNeta = 100 - porcentajeTotalComisiones;
    const calculatedUtilidadNetaConIva = Math.round(finalPriceNum * (porcentajeGananciaNeta / 100));
    setUtilidadNetaConIva(calculatedUtilidadNetaConIva);

    // Paso 3: Calcular comisión embajador con IVA
    const calculatedComisionAmbassadorConIva = Math.round(finalPriceNum * (ambassadorCommission / 100));
    setComisionAmbassadorConIva(calculatedComisionAmbassadorConIva);

    // Paso 4: Calcular comisión AW con IVA (por resta para absorber redondeos)
    const calculatedComisionAWConIva = finalPriceNum - calculatedUtilidadNetaConIva - calculatedComisionAmbassadorConIva;
    setComisionAWConIva(calculatedComisionAWConIva);

    // Paso 5: Calcular valores sin IVA
    // Precio sin IVA
    const calculatedPrecioSinIva = Math.round(finalPriceNum / 1.19);
    setPrecioSinIva(calculatedPrecioSinIva);

    // Ganancia neta sin IVA
    const calculatedUtilidadNeta = Math.round(calculatedUtilidadNetaConIva / 1.19);
    setUtilidadNeta(calculatedUtilidadNeta);

    // Comisión embajador sin IVA
    const calculatedComisionAmbassador = Math.round(calculatedComisionAmbassadorConIva / 1.19);
    setComisionAmbassador(calculatedComisionAmbassador);

    // Comisión AW sin IVA (por resta para absorber redondeos)
    const calculatedComisionAW = calculatedPrecioSinIva - calculatedUtilidadNeta - calculatedComisionAmbassador;
    setComisionAW(calculatedComisionAW);
  };

  // Manejar cambio en precio final
  const handleFinalPriceChange = (e) => {
    const newValue = e.target.value;
    setFinalPrice(newValue);
    calculateFromFinalPrice(newValue);
  };

  // Calcular porcentajes para mostrar
  const getPercentage = (value) => {
    if (!precioSinIva || precioSinIva === 0) return "0";
    return ((value / precioSinIva) * 100).toFixed(1);
  };

  // Validar antes de guardar
  const handleSave = () => {
    const finalPriceNum = parseFloat(finalPrice) || 0;

    // Validación: precio mínimo
    if (finalPriceNum < MIN_PRICE) {
      alert(
        `El precio final debe ser al menos CLP $${MIN_PRICE.toLocaleString(
          "es-CL"
        )}`
      );
      return;
    }

    // Validación: valores válidos
    if (finalPriceNum <= 0) {
      alert("El precio debe ser mayor a 0");
      return;
    }

    // Guardar todos los valores calculados
    onSave({
      precio: Math.round(finalPriceNum), // Precio final con IVA
      precioSinIva: precioSinIva,
      utilidadNeta: utilidadNeta,
      utilidadNetaConIva: utilidadNetaConIva,
      comisionAW: comisionAW,
      comisionAWConIva: comisionAWConIva,
      comisionAmbassador: comisionAmbassador,
      comisionAmbassadorConIva: comisionAmbassadorConIva,
    });

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  // Formatear números a CLP
  const formatCLP = (value) => {
    // Redondear SOLO al momento de mostrar
    const roundedValue = Math.round(value || 0);
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(roundedValue);
  };

  console.log("Render PriceCalculatorModal with:", {
    finalPrice,
    precioSinIva,
    utilidadNeta,
    utilidadNetaConIva,
    comisionAW,
    comisionAWConIva,
    comisionAmbassador,
    comisionAmbassadorConIva,
  });
  return (
    <Modal isOpen={isOpen} onClose={handleCancel} large="max-w-2xl">
      <div className="bg-aw-bgbluedark rounded-2xl p-6 inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green scrollbar-box thin h-[94dvh] overflow-y-auto">
        {/* Título */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-3">
            <FontAwesomeIcon
              icon="fa-solid fa-calculator"
              className="text-aw-green"
            />
            Calculadora de Precio
          </h2>
          <p className="text-gray-300 text-sm">
            Define el precio de tu ticket considerando las comisiones y el IVA
          </p>
        </div>

        {/* Sección 1: Información de Comisiones */}
        <div className="bg-aw-bluedark-95 rounded-xl p-4 mb-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-aw-green mb-3 flex items-center gap-2">
            <FontAwesomeIcon icon="fa-solid fa-info-circle" />
            Información de Comisiones
          </h3>

          <div className="grid grid-cols-2 gap-4">
            {/* Comisión AppWhoop */}
            <div className="bg-aw-bluedark rounded-lg p-3">
              <div className="text-gray-400 text-sm mb-1">
                Comisión AppWhoop
              </div>
              <div className="text-white font-bold text-xl">
                {APPWHOOP_COMMISSION}%
              </div>
              <div className="text-gray-500 text-xs mt-1">
                Incluye plataforma y procesamiento de pagos
              </div>
            </div>

            {/* Comisión Embajadores */}
            <div className="bg-aw-bluedark rounded-lg p-3">
              <div className="text-gray-400 text-sm mb-1">
                Comisión Embajadores
              </div>
              <div className="text-white font-bold text-xl">
                {ambassadorCommission}%
              </div>
              <div className="text-aw-green/85 text-xs mt-1">
                {ambassadorCommission === 0
                  ? "No se utilizarán embajadores"
                  : "Comisión global para los embajadores del evento"}
              </div>
            </div>
          </div>

          {/* Total de comisiones */}
          <div className="mt-3 pt-3 border-t border-gray-700">
            <div className="flex justify-between items-center">
              <span className="text-gray-300 text-sm">Comisión Total:</span>
              <span className="text-aw-green font-bold text-lg">
                {APPWHOOP_COMMISSION + ambassadorCommission}%
              </span>
            </div>
          </div>
        </div>

        {/* Sección 2: Calculadora */}
        <div className="bg-aw-bluedark-95 rounded-xl p-4 border border-gray-700">
          <h3 className="text-lg font-semibold text-aw-green mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon="fa-solid fa-calculator" />
            Cálculo de Precio
          </h3>

          <div className="space-y-4">
            {/* Input único: Precio Final (con IVA) */}
            <div>
              <label className="block text-white text-sm font-semibold mb-2">
                <FontAwesomeIcon
                  icon="fa-solid fa-dollar-sign"
                  className="text-aw-green mr-2"
                />
                Precio Final que pagará el cliente (Con IVA)
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">
                  CLP $
                </span>
                <input
                  type="number"
                  min="0"
                  step="100"
                  value={finalPrice || ""}
                  onChange={handleFinalPriceChange}
                  className="w-full bg-aw-bluedark text-white rounded-lg pl-16 pr-4 py-3 border-2 border-gray-300 focus:border-aw-green focus:outline-none text-lg font-semibold"
                  placeholder="0"
                />
              </div>
            </div>

            {/* Tabs - Solo mostrar si hay comisión de embajadores */}
            {ambassadorCommission > 0 && (
              <div className="mb-4">
                <div className="flex gap-2 border-b border-gray-700">
                  <button
                    onClick={() => setActiveTab('with-ambassador')}
                    className={`px-4 py-2 font-semibold transition-all ${
                      activeTab === 'with-ambassador'
                        ? 'text-aw-green border-b-2 border-aw-green'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-user-tie" className="mr-2" />
                    Con Embajador
                  </button>
                  <button
                    onClick={() => setActiveTab('without-ambassador')}
                    className={`px-4 py-2 font-semibold transition-all ${
                      activeTab === 'without-ambassador'
                        ? 'text-aw-green border-b-2 border-aw-green'
                        : 'text-gray-400 hover:text-gray-300'
                    }`}
                  >
                    <FontAwesomeIcon icon="fa-solid fa-store" className="mr-2" />
                    Sin Embajador
                  </button>
                </div>
              </div>
            )}

            {/* Contenido de la pestaña "Con Embajador" o sin tabs si no hay embajadores */}
            {(ambassadorCommission === 0 || activeTab === 'with-ambassador') && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Detalle 1: Precio sin IVA (Solo lectura) */}
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-receipt"
                        className="text-blue-400 mr-2"
                      />
                      Precio sin IVA
                    </label>
                    <div className="bg-aw-bluedark rounded-lg p-3 border border-gray-700">
                      <div className="text-white font-bold text-lg">
                        {formatCLP(precioSinIva)}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        Base para el cálculo de comisiones
                      </div>
                    </div>
                  </div>

                  {/* Detalle 2: Total de Comisiones (Solo lectura) */}
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-percent"
                        className="text-yellow-500 mr-2"
                      />
                      Total de Comisiones
                    </label>
                    <div className="bg-aw-bluedark rounded-lg p-3 border border-gray-700">
                      <div className="text-white font-bold text-lg">
                        {formatCLP(comisionAW + comisionAmbassador)}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        AppWhoop: {formatCLP(comisionAW)} ({APPWHOOP_COMMISSION}%)
                        {ambassadorCommission > 0 && (
                          <>
                            <br />
                            Embajadores: {formatCLP(comisionAmbassador)} ({ambassadorCommission}%)
                          </>
                        )}
                      </div>
                      <div className="text-yellow-300 text-xs mt-1 font-semibold">
                        Total comisiones + IVA: {formatCLP(comisionAWConIva + comisionAmbassadorConIva)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalle 3: Ganancia Neta (Solo lectura) - Destacado */}
                <div className="bg-gradient-to-r from-aw-green/20 to-aw-green/5 rounded-lg p-4 border-2 border-aw-green mt-4">
                  <label className="block text-white text-base font-bold mb-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-coins"
                      className="text-aw-green mr-2"
                    />
                    Tu Ganancia Neta por ticket (Sin IVA)
                  </label>
                  <div className="text-aw-green font-bold text-2xl mb-2">
                    {formatCLP(utilidadNeta)}
                  </div>
                  <div className="text-gray-300 text-xs">
                    Esto es lo que ganas por cada ticket vendido (sin IVA)
                  </div>
                  <div className="text-yellow-300 text-sm font-semibold mt-2">
                    💰 Monto que se te depositará (con IVA): {formatCLP(utilidadNetaConIva)}
                  </div>
                </div>
              </>
            )}

            {/* Contenido de la pestaña "Sin Embajador" */}
            {ambassadorCommission > 0 && activeTab === 'without-ambassador' && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Detalle 1: Precio sin IVA (Solo lectura) */}
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-receipt"
                        className="text-blue-400 mr-2"
                      />
                      Precio sin IVA
                    </label>
                    <div className="bg-aw-bluedark rounded-lg p-3 border border-gray-700">
                      <div className="text-white font-bold text-lg">
                        {formatCLP(precioSinIva)}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        Base para el cálculo de comisiones
                      </div>
                    </div>
                  </div>

                  {/* Detalle 2: Total de Comisiones (Solo lectura) - Sin embajador */}
                  <div>
                    <label className="block text-gray-300 text-sm font-semibold mb-2">
                      <FontAwesomeIcon
                        icon="fa-solid fa-percent"
                        className="text-yellow-500 mr-2"
                      />
                      Total de Comisiones
                    </label>
                    <div className="bg-aw-bluedark rounded-lg p-3 border border-gray-700">
                      <div className="text-white font-bold text-lg">
                        {formatCLP(comisionAW)}
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        AppWhoop: {formatCLP(comisionAW)} ({APPWHOOP_COMMISSION}%)
                        <br />
                        Embajadores: {formatCLP(0)} (0%)
                      </div>
                      <div className="text-yellow-300 text-xs mt-1 font-semibold">
                        Total comisiones + IVA: {formatCLP(comisionAWConIva)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalle 3: Ganancia Neta SIN embajador - Destacado */}
                <div className="bg-gradient-to-r from-aw-green/20 to-aw-green/5 rounded-lg p-4 border-2 border-aw-green mt-4">
                  <label className="block text-white text-base font-bold mb-2">
                    <FontAwesomeIcon
                      icon="fa-solid fa-coins"
                      className="text-aw-green mr-2"
                    />
                    Tu Ganancia Neta por ticket (Sin IVA)
                  </label>
                  <div className="text-aw-green font-bold text-2xl mb-2">
                    {formatCLP(Math.round((utilidadNetaConIva /1.19 )+ Math.round(comisionAmbassadorConIva /1.19) ))}
                  </div>
                  <div className="text-gray-300 text-xs">
                    Esto es lo que ganas por cada ticket vendido sin embajador (sin IVA)
                  </div>
                  <div className="text-yellow-300 text-sm font-semibold mt-2">
                    💰 Monto que se te depositará (con IVA): {formatCLP(utilidadNetaConIva + comisionAmbassadorConIva)}
                  </div>
                </div>
              </>
            )}

            {/* Nota explicativa */}
            <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-3 mt-4">
              <p className="text-blue-300 text-xs leading-relaxed flex items-start gap-2">
                <FontAwesomeIcon
                  icon="fa-solid fa-info-circle"
                  className="mt-0.5 flex-shrink-0"
                />
                <span>
                  Los valores se redondean al número entero más cercano. El precio mínimo permitido es {formatCLP(MIN_PRICE)}.
                </span>
              </p>
            </div>
            
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={handleCancel}
            className="px-6 py-2 border border-gray-600 rounded-lg text-white hover:bg-aw-bluedark transition-colors duration-300"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-bold hover:scale-95 transition-transform duration-300 flex items-center gap-2"
          >
            <FontAwesomeIcon icon="fa-solid fa-check" />
            Guardar Precio
          </button>
        </div>
      </div>
    </Modal>
  );
}
